"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FlaskConical, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  title: "Application Consultation",
  subtitle: "Method development & technical advisory",
  description: "Work directly with analytical specialists to evaluate sample suitability, design tailored testing workflows, and optimize laboratory throughput.",
  href: "/ApplicationConsultationPage",
  icon: FlaskConical,
  badgeText: "Consultation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/application_consultation_lab.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/application_consultation_lab.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/application_consultation_lab.png-wvc-sizes",
  highlights: ["Feasibility studies & sample analysis", "Process analytical technology (PAT) strategy", "Custom method validation & optimization"]
}, {
  title: "Qualification & Lifecycle Services",
  subtitle: "Compliance, maintenance & operational uptime",
  description: "Maintain regulatory compliance and peak instrument performance across your facility with certified validation protocols and preventive care.",
  href: "/QualificationLifecycleServicesPage",
  icon: ShieldCheck,
  badgeText: "Compliance & Lifecycle",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/qualification_lifecycle_services.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/qualification_lifecycle_services.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/qualification_lifecycle_services.webp-wvc-sizes",
  highlights: ["IQ / OQ / PQ validation protocols", "Routine preventative maintenance & calibration", "Audit-ready documentation & GMP support"]
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 24
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : i * 0.15,
        ease: [0.215, 0.61, 0.355, 1]
      }
    })
  };
  return <section id="relatedservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="max-w-2xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3">
            Complementary Support
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
            Related Services
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Enhance analytical accuracy and maintain strict compliance with dedicated application advisory and lifecycle support.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {services.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} data-index={index} custom={index} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-50px"
          }} variants={cardVariants} className="flex flex-col h-full">
                <Card className="flex flex-col h-full justify-between overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow">
                  <div className="relative h-56 w-full overflow-hidden bg-muted">
                    <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-105" />
                    <div className="absolute top-4 left-4">
                      <Badge variant="default" className="shadow-sm">
                        {service.badgeText}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pt-6 pb-2">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                        <IconComponent className="size-5 text-primary" />
                      </div>
                      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {service.subtitle}
                      </span>
                    </div>
                    <CardTitle className="text-2xl font-bold text-foreground font-default">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm md:text-base text-muted-foreground mt-2 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="py-4">
                    <div className="border-t border-border pt-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                        Key Deliverables
                      </p>
                      <ul className="space-y-2.5">
                        {service.highlights.map((item, itemIdx) => <li key={item} data-index={itemIdx} className="flex items-start gap-2.5 text-sm text-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 pb-6">
                    <Button asChild className="w-full justify-between group">
                      <Link to={service.href}>
                        <span>Explore {service.title}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}